import React, { useEffect, useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  Download,
  Save,
  X,
  CreditCard,
  User,
  DollarSign,
  Calendar,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { creditsAPI, clientsAPI } from '../services/api';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';
import EntitySelectWithAdd from '../components/ui/EntitySelectWithAdd';

const defaultForm = {
  client_id: '',
  reference_type: 'sales_order',
  reference_id: '',
  original_amount: '',
  current_balance: '',
  date: '',
  note: '',
};

const Credits = () => {
  const [credits, setCredits] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedCredit, setSelectedCredit] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch credits
  const fetchCredits = async () => {
    setLoading(true);
    try {
      const res = await creditsAPI.getCredits();
      setCredits(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des crédits');
    }
    setLoading(false);
  };

  // Fetch clients for dropdown
  const fetchClients = async () => {
    try {
      const res = await clientsAPI.getClients();
      setClients(res.data);
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    fetchCredits();
    fetchClients();
  }, []);

  // Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Handle add/edit submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      if (editingId) {
        await creditsAPI.updateCredit(editingId, {
          current_balance: form.current_balance,
          note: form.note,
          date: form.date
        });
        toast.success('Crédit modifié');
      } else {
        await creditsAPI.createCredit(form);
        toast.success('Crédit ajouté');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      fetchCredits();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (credit) => {
    setForm({
      client_id: credit.client_id,
      reference_type: credit.reference_type,
      reference_id: credit.reference_id,
      original_amount: credit.original_amount,
      current_balance: credit.current_balance,
      date: credit.date ? credit.date.slice(0, 10) : '',
      note: credit.note || '',
    });
    setEditingId(credit.id);
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce crédit ? Cette action est irréversible.')) return;
    try {
      await creditsAPI.deleteCredit(id);
      toast.success('Crédit supprimé');
      fetchCredits();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle add new
  const handleAdd = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(true);
  };

  const handleViewDetails = (credit) => {
    setSelectedCredit(credit);
    setDetailsModalOpen(true);
  };

  // Helper to get client name
  const getClientName = (id) => {
    const client = clients.find((c) => c.id === id);
    return client ? client.name : id;
  };

  // Get credit status badge
  const getCreditStatusBadge = (credit) => {
    if (credit.current_balance <= 0) {
      return <ModernBadge variant="success">Payé</ModernBadge>;
    } else if (credit.current_balance < credit.original_amount) {
      return <ModernBadge variant="warning">Partiel</ModernBadge>;
    } else {
      return <ModernBadge variant="danger">En attente</ModernBadge>;
    }
  };

  const filteredCredits = credits.filter(credit => {
    const clientName = getClientName(credit.client_id);
    return String(clientName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(credit.original_amount || '').includes(searchTerm) ||
           String(credit.current_balance || '').includes(searchTerm) ||
           String(credit.date || '').includes(searchTerm) ||
           String(credit.reference_label || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  const paginatedCredits = filteredCredits.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredCredits.length / itemsPerPage);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const clientOptions = clients.map(client => ({
    value: client.id,
    label: client.name
  }));

  const referenceTypeOptions = [
    { value: 'sales_order', label: 'Commande' },
    { value: 'sales_invoice', label: 'Facture' }
  ];

  const tableColumns = [
    {
      key: 'client_id',
      label: 'Client',
      render: (value) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
            <User className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{getClientName(value)}</div>
          </div>
        </div>
      )
    },
    {
      key: 'reference_label',
      label: 'Référence',
      render: (value) => (
        <div className="flex items-center">
          <div className="w-8 h-8 bg-neutral-100 rounded-lg flex items-center justify-center mr-2">
            <CreditCard className="w-4 h-4 text-neutral-600" />
          </div>
          <span className="font-medium text-neutral-900">{value}</span>
        </div>
      )
    },
    {
      key: 'original_amount',
      label: 'Montant Original',
      render: (value) => (
        <span className="font-semibold text-neutral-700">
          {formatCurrency(value)}
        </span>
      )
    },
    {
      key: 'current_balance',
      label: 'Solde Actuel',
      render: (value, credit) => {
        const color = value <= 0 ? 'text-success-600' : value < credit.original_amount ? 'text-warning-600' : 'text-danger-600';
        return (
          <span className={`font-semibold ${color}`}>
            {formatCurrency(value)}
          </span>
        );
      }
    },
    {
      key: 'status',
      label: 'Statut',
      render: (value, credit) => getCreditStatusBadge(credit)
    },
    {
      key: 'date',
      label: 'Date',
      render: (value) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{formatDate(value)}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, credit) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleViewDetails(credit)}
          >
            Détails
          </ModernButton>
          <ModernButton
            variant="primary"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(credit)}
          >
            Modifier
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(credit.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  const totalCredits = credits.reduce((sum, credit) => sum + (Number(credit.original_amount) || 0), 0);
  const totalBalance = credits.reduce((sum, credit) => sum + (Number(credit.current_balance) || 0), 0);
  const totalClients = new Set(credits.map(c => c.client_id)).size;
  const activeCredits = credits.filter(credit => credit.current_balance > 0).length;

  const handleExport = async () => {
    try {
      const response = await creditsAPI.exportCredits();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'credits.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error("Erreur lors de l'export des crédits");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Crédits</h1>
          <p className="text-neutral-600">Gérez les crédits accordés aux clients</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des crédits..."
            onSearch={setSearchTerm}
            className="w-64"
          />
          <ModernButton
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleAdd}
          >
            Ajouter un crédit
          </ModernButton>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Crédits</p>
              <p className="text-2xl font-bold text-neutral-900">{credits.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Montant Total</p>
              <p className="text-2xl font-bold text-success-600">
                {formatCurrency(totalCredits)}
              </p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Solde Restant</p>
              <p className="text-2xl font-bold text-warning-600">{formatCurrency(totalBalance)}</p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Crédits Actifs</p>
              <p className="text-2xl font-bold text-info-600">{activeCredits}</p>
            </div>
            <div className="w-12 h-12 bg-info-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-info-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Credits Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Liste des crédits</h3>
            <p className="text-sm text-neutral-600">{filteredCredits.length} crédits trouvés</p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
              Filtrer
            </ModernButton>
            <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />} onClick={handleExport} title="Exporter les crédits au format CSV">
              Exporter
            </ModernButton>
          </div>
        </div>

        <ModernTable
          columns={tableColumns}
          data={paginatedCredits}
          className="w-full"
        />
        {paginatedCredits.length === 0 && (
          <div className="text-center text-neutral-400 py-8">Aucun crédit trouvé.</div>
        )}
        {totalPages > 1 && (
          <div className="flex justify-center mt-4 space-x-2">
            <ModernButton variant="secondary" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Précédent</ModernButton>
            <span className="px-2 py-1">Page {currentPage} / {totalPages}</span>
            <ModernButton variant="secondary" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Suivant</ModernButton>
          </div>
        )}
      </ModernCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingId ? 'Modifier le crédit' : 'Ajouter un crédit'}
        size="full"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <EntitySelectWithAdd
              entityType="client"
              label="Client"
              options={clientOptions}
              value={form.client_id}
              onChange={value => setForm(f => ({ ...f, client_id: value }))}
              fetchOptions={fetchClients}
              required
              showAddButton={true}
            />

            <ModernSelect
              label="Type de Référence"
              name="reference_type"
              value={referenceTypeOptions.find(opt => opt.value === form.reference_type) || null}
              onChange={option => setForm(f => ({ ...f, reference_type: option ? option.value : '' }))}
              options={referenceTypeOptions}
              required
            />

            <ModernInput
              label="ID de Référence"
              name="reference_id"
              type="number"
              min="1"
              value={form.reference_id}
              onChange={handleChange}
              placeholder="ID de la commande ou facture"
              required
            />

            <ModernInput
              label="Montant Original"
              name="original_amount"
              type="number"
              min="0"
              step="0.01"
              value={form.original_amount}
              onChange={handleChange}
              placeholder="0.00"
              icon={<DollarSign className="w-5 h-5" />}
              required
            />

            <ModernInput
              label="Solde Actuel"
              name="current_balance"
              type="number"
              min="0"
              step="0.01"
              value={form.current_balance}
              onChange={handleChange}
              placeholder="0.00"
              icon={<DollarSign className="w-5 h-5" />}
              required
            />

            <ModernInput
              label="Date"
              name="date"
              type="date"
              value={form.date}
              onChange={handleChange}
              icon={<Calendar className="w-5 h-5" />}
              required
            />

            <ModernInput
              label="Note"
              name="note"
              value={form.note}
              onChange={handleChange}
              placeholder="Ajouter une note (optionnel)"
              className="md:col-span-2"
            />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => setShowForm(false)}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={<Save className="w-4 h-4" />}
              disabled={submitLoading}
            >
              {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
            </ModernButton>
          </div>
        </form>
      </ModernModal>

      {/* Details Modal */}
      <ModernModal isOpen={detailsModalOpen} onClose={() => setDetailsModalOpen(false)} title="Détails du crédit" size="md">
        {selectedCredit && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">ID</label>
                <p className="text-sm text-gray-900">{selectedCredit.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Client</label>
                <p className="text-sm text-gray-900">{getClientName(selectedCredit.client_id)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Référence</label>
                <p className="text-sm text-gray-900">{selectedCredit.reference_label}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <p className="text-sm text-gray-900">{selectedCredit.reference_type === 'sales_order' ? 'Commande' : 'Facture'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Montant Original</label>
                <p className="text-sm font-semibold text-gray-900">{formatCurrency(selectedCredit.original_amount)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Solde Actuel</label>
                <p className={`text-sm font-semibold ${selectedCredit.current_balance <= 0 ? 'text-green-600' : selectedCredit.current_balance < selectedCredit.original_amount ? 'text-yellow-600' : 'text-red-600'}`}>
                  {formatCurrency(selectedCredit.current_balance)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Date</label>
                <p className="text-sm text-gray-900">{formatDate(selectedCredit.date)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Statut</label>
                <div className="mt-1">{getCreditStatusBadge(selectedCredit)}</div>
              </div>
            </div>
            {selectedCredit.note && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Note</label>
                <p className="text-sm text-gray-900">{selectedCredit.note}</p>
              </div>
            )}
          </div>
        )}
      </ModernModal>
    </div>
  );
};

export default Credits; 